/**
 * SfxKeys — pure registry of combat + UI sound effect keys for MANOR v1.
 * No Phaser import: safe to use in node/Vitest.
 *
 * SET-1 (SFX_KEYS): 21 combat/UI clips — all prefixed "sfx_".
 * SET-2 (ENV_SFX_KEYS): 15 environmental/weapon clips — intentionally omit
 * the "sfx_" prefix to match the asset filenames shipped in public/assets/sfx/.
 *
 * AudioManager preloads ALL_SFX_MANIFEST (SET-1 + SET-2) on scene boot.
 *
 * TODO(MY-1459): Drop the SET-1 .ogg files into public/assets/sfx/.
 *   Source: WAV masters → ffmpeg -i master.wav -c:a libvorbis -q:a 6 <key>.ogg
 *   License options: freesound.org (CC0), ZapSplat (attribution), or bespoke foley.
 */

export const SFX_KEYS = [
  // ── Combat ──────────────────────────────────────────────────────────
  'sfx_punch_light',
  'sfx_punch_medium',
  'sfx_punch_heavy',
  'sfx_kick',
  'sfx_throw',
  'sfx_weapon_hit_bat',
  'sfx_weapon_hit_hammer',
  'sfx_weapon_hit_blade',
  'sfx_block',
  'sfx_parry_flash',
  'sfx_ko',
  // ── Movement ────────────────────────────────────────────────────────
  'sfx_jump',
  'sfx_land',
  'sfx_pickup',
  // ── UI ──────────────────────────────────────────────────────────────
  'sfx_menu_select',
  'sfx_menu_confirm',
  'sfx_pause',
  'sfx_hint_appear',
  // ── Power / progression ──────────────────────────────────────────────
  'sfx_star_charge',
  'sfx_super_cast',
  'sfx_level_clear',
] as const;

/**
 * SET-2 environmental and weapon SFX keys (MY-1968).
 * Runtime keys omit the "sfx_" prefix; shipped filenames keep it for consistency
 * with the original generated asset batch.
 */
export const ENV_SFX_KEYS = [
  // ── Vehicles ─────────────────────────────────────────────────────────
  'car_engine',
  'car_rev',
  'bike_engine',
  'bike_rev',
  // ── Firearms ─────────────────────────────────────────────────────────
  'gunshot',
  'flare_gun',
  // ── Blade ────────────────────────────────────────────────────────────
  'stab',
  // ── Stage 4 ambient (Northern Line) ──────────────────────────────────
  'train_rumble',
  'train_brake',
  // ── Smashables (by material) ─────────────────────────────────────────
  'glass_smash',
  'wood_break',
  'metal_clang',
  'bottle_break',
  'prop_break',
  // ── Explosions ───────────────────────────────────────────────────────
  'explosion',
] as const;

export type SfxKey = (typeof SFX_KEYS)[number] | (typeof ENV_SFX_KEYS)[number];

export interface SfxEntry {
  key: SfxKey;
  /** Path relative to public root (Vite serves public/ without bundling). */
  path: string;
}

/** SET-1 manifest — original 21 combat/UI clips. */
export const SFX_MANIFEST: readonly SfxEntry[] = SFX_KEYS.map((key) => ({
  key,
  path: `assets/sfx/${key}.ogg`,
}));

/** SET-2 manifest — 15 environmental/weapon clips. */
export const ENV_SFX_MANIFEST: readonly SfxEntry[] = ENV_SFX_KEYS.map((key) => ({
  key,
  path: `assets/sfx/sfx_${key}.ogg`,
}));

/** Full manifest (SET-1 + SET-2) — AudioManager iterates this to preload every clip. */
export const ALL_SFX_MANIFEST: readonly SfxEntry[] = [...SFX_MANIFEST, ...ENV_SFX_MANIFEST];

/** SFX fired when the player earns a 1-up at a score threshold. Reuses the level-clear cue. */
export const SFX_1UP_KEY: SfxKey = 'sfx_level_clear';
